import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import {
  SpaceAreaChangeReadModel,
  SpaceAreaChangeTag,
  useGetSpacesByIdAreaChangesQuery,
  usePutSpacesByIdAreaChangesAndChangeIdTagMutation,
} from '@client/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  toNumberString,
} from '@client/toolkit';

const AREA_CHANGE_TAGS: SpaceAreaChangeTag[] = ['Reconstruction', 'ControlMeasurement', 'UserError'];

export interface SpaceAreaHistoryCardProps {
  className?: string;
  spaceId?: string;
}

function AreaChangeRowSkeleton() {
  return (
    <TableRow>
      {Array.from(Array(4).keys()).map((idx) => (
        <TableCell key={idx}>
          <Skeleton className="w-full h-full" />
        </TableCell>
      ))}
    </TableRow>
  );
}

export function SpaceAreaHistoryCard({ className, spaceId }: SpaceAreaHistoryCardProps) {
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tType } = useTranslation('type');

  const { data: changes, isFetching, isLoading } = useGetSpacesByIdAreaChangesQuery(
    { id: spaceId ?? '' },
    { skip: !spaceId },
  );

  const [setTag] = usePutSpacesByIdAreaChangesAndChangeIdTagMutation();

  const unit = tType('AreaMeasurementUnit.SquareMeters', { context: 'short' });
  const formatArea = (value?: number | null) =>
    value == null ? '\u2013' : toNumberString(value, undefined, { unit });

  const handleTagChange = (change: SpaceAreaChangeReadModel, value: string) => {
    if (!spaceId) return;

    setTag({
      id: spaceId,
      changeId: change.id,
      body: { tag: value === '' ? null : (value as SpaceAreaChangeTag) },
    });
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex-1" variant="dense" loading={!ready}>
          {t('space.areaHistory.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table className="overflow-hidden w-full" size="xs" layout="fixed">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{t('space.areaHistory.date')}</TableHeadCell>
              <TableHeadCell>{t('space.areaHistory.change')}</TableHeadCell>
              <TableHeadCell>{t('space.areaHistory.user')}</TableHeadCell>
              <TableHeadCell>{t('space.areaHistory.tag')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          {!isLoading && (!changes || changes.length === 0) && (
            <caption className="px-4 py-4 [caption-side:bottom]">
              <EmptyPlaceholder>{t('space.areaHistory.noChanges')}</EmptyPlaceholder>
            </caption>
          )}
          <TableBody>
            {isFetching
              ? Array.from(Array(3).keys()).map((idx) => <AreaChangeRowSkeleton key={idx} />)
              : changes?.map((change) => (
                  <TableRow key={change.id}>
                    <TableCell>{format(parseISO(change.logTime), 'dd.MM.yyyy HH:mm')}</TableCell>
                    <TableCell>
                      {formatArea(change.oldAreaMetric)}
                      {' \u2192 '}
                      {formatArea(change.newAreaMetric)}
                    </TableCell>
                    <TableCell>{change.user}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        aria-label={t('space.areaHistory.tag')}
                        value={change.tag ?? ''}
                        onChange={(event) => handleTagChange(change, event.target.value)}
                      >
                        <option value="">{t('space.areaHistory.noTag')}</option>
                        {AREA_CHANGE_TAGS.map((tag) => (
                          <option key={tag} value={tag}>
                            {tType(`SpaceAreaChangeTag.${tag}`)}
                          </option>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
